// src/services/surveyResponseService.js
import { surveyResponsesAPI } from './api';

export const surveyResponseService = {
  // ===== PUBLIC SURVEY SUBMISSION ENDPOINTS =====
  
  // Submit public survey responses
  async submitPublicSurveyResponse(submissionData) {
    try {
      console.log('📤 Submitting public survey response (PascalCase):', JSON.stringify(submissionData, null, 2));
      
      // Log the structure for debugging
      console.log('📋 Submission data structure:', {
        SurveyId: submissionData.SurveyId,
        Token: submissionData.Token,
        EncodedEmployeeId: submissionData.EncodedEmployeeId,
        ResponsesCount: submissionData.Responses?.length,
        ResponsesSample: submissionData.Responses?.slice(0, 2)
      });
      
      const response = await surveyResponsesAPI.submitPublicResponse(submissionData);
      console.log('✅ Public survey response submitted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error submitting public survey response:', error);
      console.error('🔍 Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      // Extract backend error message if available
      const backendMessage = error.response?.data?.message || error.response?.data?.errors || error.response?.data;
      throw new Error(backendMessage || 'Failed to submit survey response');
    }
  },

  // Get public survey status
  async getPublicSurveyStatus(surveyId, token, encodedEmployeeId) {
    try {
      console.log('🔍 Checking public survey status:', { surveyId, token, encodedEmployeeId });
      const response = await surveyResponsesAPI.getPublicSurveyStatus(surveyId, token, encodedEmployeeId);
      console.log('✅ Public survey status loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching public survey status:', error);
      // Return default status if endpoint doesn't exist yet
      return {
        isCompleted: false,
        completedDate: null,
        canSubmit: true
      };
    }
  },

  // Validate public survey access
  async validatePublicSurveyAccess(surveyId, token, encodedEmployeeId) {
    try {
      console.log('🔐 Validating public survey access:', { surveyId, token, encodedEmployeeId });
      const response = await surveyResponsesAPI.validatePublicSurveyAccess(surveyId, token, encodedEmployeeId);
      console.log('✅ Public survey access validation result:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error validating public survey access:', error);
      throw new Error(error.response?.data?.message || 'Failed to validate survey access');
    }
  },

  // ===== EXISTING DASHBOARD ENDPOINTS =====

  // Get survey response dashboard data
  async getSurveyResponseDashboard(surveyId) {
    try {
      console.log('🔄 Fetching survey response dashboard for:', surveyId);
      const response = await surveyResponsesAPI.getDashboard(surveyId);
      console.log('✅ Survey response dashboard loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey response dashboard:', error);
      throw new Error('Failed to load survey response dashboard: ' + error.message);
    }
  },

  // Get filtered survey responses by employee IDs
  async getFilteredSurveyResponses(surveyId, employeeIds) {
    try {
      console.log('🔄 Fetching filtered survey responses for:', surveyId, 'employees:', employeeIds);
      const response = await surveyResponsesAPI.getFilteredResponses(surveyId, { employeeIds });
      console.log('✅ Filtered survey responses loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching filtered survey responses:', error);
      throw new Error('Failed to load filtered survey responses: ' + error.message);
    }
  },

  // Get individual survey response details
  async getSurveyResponseDetails(responseId) {
    try {
      console.log('🔄 Fetching survey response details for:', responseId);
      const response = await surveyResponsesAPI.getResponseDetails(responseId);
      console.log('✅ Survey response details loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey response details:', error);
      throw new Error('Failed to load survey response details: ' + error.message);
    }
  },

  // Get response statistics
  async getResponseStatistics(surveyId) {
    try {
      console.log('🔄 Fetching response statistics for:', surveyId);
      const response = await surveyResponsesAPI.getStatistics(surveyId);
      console.log('✅ Response statistics loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching response statistics:', error);
      throw new Error('Failed to load response statistics: ' + error.message);
    }
  },

  // Export survey responses
  async exportSurveyResponses(surveyId, format = 'excel') {
    try {
      console.log('🔄 Exporting survey responses for:', surveyId, 'format:', format);
      const response = await surveyResponsesAPI.exportResponses(surveyId, format);
      
      if (!(response.data instanceof Blob)) {
        throw new Error('Export response is not a Blob');
      }
      
      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `survey-${surveyId}-responses.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Survey responses exported successfully');
      return { success: true, message: 'Survey responses exported successfully' };
    } catch (error) {
      console.error('❌ Error exporting survey responses:', error);
      throw new Error('Failed to export survey responses: ' + error.message);
    }
  },

  // Get response trends over time
  async getResponseTrends(surveyId, period = 'monthly') {
    try {
      console.log('🔄 Fetching response trends for:', surveyId, 'period:', period);
      const response = await surveyResponsesAPI.getTrends(surveyId, period);
      console.log('✅ Response trends loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching response trends:', error);
      throw new Error('Failed to load response trends: ' + error.message);
    }
  },

  // Get category-wise analysis
  async getCategoryAnalysis(surveyId, categoryId = null) {
    try {
      console.log('🔄 Fetching category analysis for:', surveyId, 'category:', categoryId);
      const response = await surveyResponsesAPI.getCategoryAnalysis(surveyId, categoryId);
      console.log('✅ Category analysis loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching category analysis:', error);
      throw new Error('Failed to load category analysis: ' + error.message);
    }
  },

  // Get employee response status
  async getEmployeeResponseStatus(surveyId) {
    try {
      console.log('🔄 Fetching employee response status for:', surveyId);
      const response = await surveyResponsesAPI.getEmployeeStatus(surveyId);
      console.log('✅ Employee response status loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching employee response status:', error);
      throw new Error('Failed to load employee response status: ' + error.message);
    }
  },

  // Send response reminders
  async sendResponseReminders(surveyId, employeeIds = []) {
    try {
      console.log('🔄 Sending response reminders for:', surveyId, 'employees:', employeeIds);
      const response = await surveyResponsesAPI.sendReminders(surveyId, { employeeIds });
      console.log('✅ Response reminders sent:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error sending response reminders:', error);
      throw new Error('Failed to send response reminders: ' + error.message);
    }
  },

  // Get comparative analysis (if multiple surveys)
  async getComparativeAnalysis(surveyIds) {
    try {
      console.log('🔄 Fetching comparative analysis for surveys:', surveyIds);
      const response = await surveyResponsesAPI.getComparativeAnalysis(surveyIds);
      console.log('✅ Comparative analysis loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching comparative analysis:', error);
      throw new Error('Failed to load comparative analysis: ' + error.message);
    }
  },

  // Get word cloud data from open-ended responses
  async getWordCloudData(surveyId, questionId = null) {
    try {
      console.log('🔄 Fetching word cloud data for:', surveyId, 'question:', questionId);
      const response = await surveyResponsesAPI.getWordCloud(surveyId, questionId);
      console.log('✅ Word cloud data loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching word cloud data:', error);
      throw new Error('Failed to load word cloud data: ' + error.message);
    }
  },

  // Get sentiment analysis
  async getSentimentAnalysis(surveyId) {
    try {
      console.log('🔄 Fetching sentiment analysis for:', surveyId);
      const response = await surveyResponsesAPI.getSentimentAnalysis(surveyId);
      console.log('✅ Sentiment analysis loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching sentiment analysis:', error);
      throw new Error('Failed to load sentiment analysis: ' + error.message);
    }
  },

  // Delete survey response
  async deleteSurveyResponse(responseId) {
    try {
      console.log('🔄 Deleting survey response:', responseId);
      const response = await surveyResponsesAPI.deleteResponse(responseId);
      console.log('✅ Survey response deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting survey response:', error);
      throw new Error('Failed to delete survey response: ' + error.message);
    }
  },

  // Bulk delete survey responses
  async bulkDeleteSurveyResponses(responseIds) {
    try {
      console.log('🔄 Bulk deleting survey responses:', responseIds);
      const response = await surveyResponsesAPI.bulkDeleteResponses({ responseIds });
      console.log('✅ Survey responses bulk deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error bulk deleting survey responses:', error);
      throw new Error('Failed to bulk delete survey responses: ' + error.message);
    }
  },

  // Get response completion rate
  async getCompletionRate(surveyId) {
    try {
      console.log('🔄 Fetching completion rate for:', surveyId);
      const response = await surveyResponsesAPI.getCompletionRate(surveyId);
      console.log('✅ Completion rate loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching completion rate:', error);
      throw new Error('Failed to load completion rate: ' + error.message);
    }
  },

  // Get average response time
  async getAverageResponseTime(surveyId) {
    try {
      console.log('🔄 Fetching average response time for:', surveyId);
      const response = await surveyResponsesAPI.getAverageResponseTime(surveyId);
      console.log('✅ Average response time loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching average response time:', error);
      throw new Error('Failed to load average response time: ' + error.message);
    }
  },
  // Add this export function to your surveyResponseService
async exportSurveyResponses(surveyId, format = 'excel') {
  try {
    console.log('🔄 Exporting survey responses for:', surveyId, 'format:', format);
    const response = await surveyResponsesAPI.exportResponses(surveyId, format);
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `survey-${surveyId}-responses.${format === 'excel' ? 'xlsx' : 'csv'}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Survey responses exported successfully');
    return { success: true, message: 'Survey responses exported successfully' };
  } catch (error) {
    console.error('❌ Error exporting survey responses:', error);
    throw new Error('Failed to export survey responses: ' + error.message);
  }
}
};
